/** Human names for event families and event codes.
 *
 *  `Notification.EVENTS` (backend, models.py) is the authoritative name when it
 *  has one — but it is a hand-maintained choices tuple written for the
 *  dashboard's dropdown and never extended as families were added, so it covers
 *  only 18 of ~288 codes. Falling back to the raw code made the timeline look
 *  half-finished: "Magic link is generated for a user" next to
 *  "tour_operator.created".
 *
 *  So: use the curated name when it exists, otherwise derive one. The code is
 *  never lost — it stays in the row's tooltip and in the inspector.
 */
import type { EventPosition } from './types';

export const FAMILY_NAMES: Record<string, string> = {
  tour_participant: 'Booking (per guest)',
  order: 'Order',
  departure: 'Departure (all guests)',
  user_departure: 'Departure (per user)',
  tour_departure: 'Departure (per tour)',
  booking: 'Booking',
  user: 'User',
  tour_operator: 'Tour operator',
  beat: 'Itinerary beat',
};

export function familyName(family: string): string {
  return FAMILY_NAMES[family] ?? family;
}

/** `tour_operator.created` → `created`; `booking.precheckin_completed` →
 *  `precheckin completed`. */
function humanizeSuffix(code: string): string {
  return code.split('.').slice(1).join('.').replace(/_/g, ' ').trim();
}

export interface LabelSource {
  code: string;
  family: string;
  /** The curated name from `Notification.EVENTS`, when it has one. */
  label?: string | null;
  position?: EventPosition;
}

/** True when the derived name is just the family — the caller may then omit it
 *  inside a group already headed by that family. */
export function labelIsFamilyName(entry: LabelSource): boolean {
  return !entry.label && entry.position?.band === 'lifecycle';
}

export function eventDisplayLabel(entry: LabelSource): string {
  if (entry.label) return entry.label;

  const band = entry.position?.band;

  // Timed events: the moment is already spelled out in the offset column, and
  // the family badge is a single letter — so the family, spelled out, is the
  // piece the row is missing. "Order" / "Departure (per user)" at 72h.
  if (band === 'lifecycle') return familyName(entry.family);

  // Action-based: read as the curated ones do — "Tour operator created".
  const suffix = humanizeSuffix(entry.code);
  if (band === 'reactive' && suffix) return `${familyName(entry.family)} ${suffix}`;

  // Unknown / itinerary codes: nothing better than what was written.
  return entry.code;
}
